import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronRight, ChevronLeft, Search as SearchIcon, ExternalLink } from "lucide-react";

/** Cross-origin feed published by the marketing site (single source of truth). */
const HELP_JSON_URL = "https://editlock.io/help/content.json";
const HELP_CENTER_URL = "https://editlock.io/help";

/** Preferred category order — mirrors the marketing /help index. */
const PREFERRED = [
  "Getting started",
  "Projects",
  "Uploading",
  "Folders & organization",
  "Sharing & review",
  "Versions",
  "Account & settings",
  "Billing",
];

export type HelpArticle = {
  slug: string;
  title: string;
  description: string;
  category: string;
  keywords: string[];
  order: number;
  updatedDate: string | null;
  url: string;
  body: string;
};

function useHelpContent() {
  return useQuery({
    queryKey: ["help-content"],
    queryFn: async (): Promise<HelpArticle[]> => {
      const res = await fetch(HELP_JSON_URL, { headers: { Accept: "application/json" } });
      if (!res.ok) throw new Error(`Help content ${res.status}`);
      const data = (await res.json()) as { articles?: HelpArticle[] };
      return data.articles ?? [];
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: 1,
  });
}

/**
 * Help section of the widget: runtime-fetched search + browse + article render.
 * Fully self-contained scroll layout (fills the widget content area). Article
 * links to other help pages open in-widget instead of navigating away.
 */
export function HelpSection({ onGoToMessage }: { onGoToMessage: () => void }) {
  const { data: articles, isLoading, isError } = useHelpContent();
  const [q, setQ] = useState("");
  const [slug, setSlug] = useState<string | null>(null);

  const current = slug && articles ? articles.find((a) => a.slug === slug) ?? null : null;

  const results = useMemo(() => {
    if (!articles) return [];
    const term = q.trim().toLowerCase();
    if (!term) return [];
    return articles.filter((a) =>
      `${a.title} ${a.description} ${a.keywords.join(" ")} ${a.category}`.toLowerCase().includes(term)
    );
  }, [articles, q]);

  const grouped = useMemo(() => {
    if (!articles) return [];
    const cats = [...new Set(articles.map((a) => a.category))].sort((a, b) => {
      const ia = PREFERRED.indexOf(a);
      const ib = PREFERRED.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    });
    return cats.map((c) => ({
      category: c,
      items: articles.filter((a) => a.category === c).sort((x, y) => x.order - y.order),
    }));
  }, [articles]);

  if (current) {
    return <ArticleView article={current} onBack={() => setSlug(null)} onOpenSlug={setSlug} />;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-3">
        <div className="relative">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search for help"
            aria-label="Search for help"
            className="w-full rounded-lg border border-border bg-muted/40 py-2 pl-9 pr-3 text-sm outline-none transition-colors focus:border-ring"
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading && <p className="p-3 text-sm text-muted-foreground">Loading help articles…</p>}

        {isError && <FallbackNotice onGoToMessage={onGoToMessage} />}

        {!isLoading && !isError && q.trim() && (
          results.length ? (
            <ul className="space-y-0.5">
              {results.map((a) => (
                <ArticleRow key={a.slug} title={a.title} onClick={() => setSlug(a.slug)} />
              ))}
            </ul>
          ) : (
            <div className="p-3 text-sm text-muted-foreground">
              No results for “{q.trim()}”.{" "}
              <button type="button" onClick={onGoToMessage} className="text-foreground underline underline-offset-2">
                Message us
              </button>{" "}
              and we’ll help.
            </div>
          )
        )}

        {!isLoading && !isError && !q.trim() && (
          <div className="space-y-5">
            {grouped.map((g) => (
              <div key={g.category}>
                <h3 className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  {g.category}
                </h3>
                <ul className="space-y-0.5">
                  {g.items.map((a) => (
                    <ArticleRow key={a.slug} title={a.title} onClick={() => setSlug(a.slug)} />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ArticleRow({ title, onClick }: { title: string; onClick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-sm transition-colors hover:bg-muted"
      >
        <span className="min-w-0">{title}</span>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>
    </li>
  );
}

function ArticleView({
  article,
  onBack,
  onOpenSlug,
}: {
  article: HelpArticle;
  onBack: () => void;
  onOpenSlug: (slug: string) => void;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> All articles
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
        <h1 className="mb-1 text-lg font-semibold">{article.title}</h1>
        {article.updatedDate && (
          <p className="mb-4 text-xs text-muted-foreground">Updated {article.updatedDate}</p>
        )}
        <div className="text-sm leading-relaxed text-foreground/90">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents(onOpenSlug)}>
            {article.body}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

function mdComponents(onOpenSlug: (slug: string) => void): Components {
  return {
    h1: ({ node: _n, ...p }) => <h2 className="mb-2 mt-5 text-base font-semibold" {...p} />,
    h2: ({ node: _n, ...p }) => <h2 className="mb-2 mt-5 text-base font-semibold" {...p} />,
    h3: ({ node: _n, ...p }) => <h3 className="mb-1.5 mt-4 text-sm font-semibold" {...p} />,
    p: ({ node: _n, ...p }) => <p className="mb-3" {...p} />,
    ul: ({ node: _n, ...p }) => <ul className="mb-3 list-disc space-y-1 pl-5" {...p} />,
    ol: ({ node: _n, ...p }) => <ol className="mb-3 list-decimal space-y-1 pl-5" {...p} />,
    strong: ({ node: _n, ...p }) => <strong className="font-semibold text-foreground" {...p} />,
    blockquote: ({ node: _n, ...p }) => (
      <blockquote className="my-3 border-l-2 border-border pl-3 italic text-muted-foreground" {...p} />
    ),
    code: ({ node: _n, ...p }) => <code className="rounded bg-muted px-1 py-0.5 text-[0.85em]" {...p} />,
    img: ({ node: _n, ...p }) => (
      // eslint-disable-next-line jsx-a11y/alt-text
      <img className="my-3 rounded-lg border border-border" loading="lazy" {...p} />
    ),
    a: ({ node: _n, href, children, ...rest }) => {
      const m = href?.match(/^https?:\/\/editlock\.io\/help\/([^/]+)\/?$/);
      if (m) {
        return (
          <button
            type="button"
            onClick={() => onOpenSlug(m[1])}
            className="text-primary underline underline-offset-2 transition-opacity hover:opacity-80"
          >
            {children}
          </button>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 transition-opacity hover:opacity-80"
          {...rest}
        >
          {children}
        </a>
      );
    },
  };
}

function FallbackNotice({ onGoToMessage }: { onGoToMessage: () => void }) {
  return (
    <div className="space-y-3 p-3 text-sm text-muted-foreground">
      <p>We couldn’t load the help articles right now.</p>
      <a
        href={HELP_CENTER_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-foreground underline underline-offset-2"
      >
        Open the help center <ExternalLink className="h-3.5 w-3.5" />
      </a>
      <p>
        or{" "}
        <button type="button" onClick={onGoToMessage} className="text-foreground underline underline-offset-2">
          message us
        </button>
        .
      </p>
    </div>
  );
}
